import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Car, Users, CalendarCheck, HandCoins, TrendingUp, AlertTriangle, Clock, ArrowRight, IndianRupee, CircleDot } from "lucide-react";
import { api } from "@/lib/api";
import { formatPriceLakh, formatPriceFull } from "@/lib/format";

export default function DashboardPage() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="text-gray-500">Loading…</div>;
  const c = s.counts;

  return (
    <div className="space-y-6" data-testid="admin-dashboard-page">
      <div>
        <h1 className="font-display font-extrabold text-2xl text-gray-900">Dealership Overview</h1>
        <p className="text-sm text-gray-500 mt-1">Real-time inventory and lead performance.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-3">
        <Kpi icon={Car} label="Active Cars" value={c.available + c.reserved} accent primary />
        <Kpi icon={IndianRupee} label="Inventory Value" value={formatPriceLakh(s.inventory_value)} />
        <Kpi icon={CircleDot} label="Sold" value={c.sold} />
        <Kpi icon={CircleDot} label="Reserved" value={c.reserved} warn />
        <Kpi icon={Users} label="New Leads" value={c.new_leads} />
        <Kpi icon={HandCoins} label="Sell Requests" value={c.sell_requests_new} />
        <Kpi icon={CalendarCheck} label="Upcoming Test Drives" value={c.upcoming_test_drives} />
        <Kpi icon={TrendingUp} label="Potential Margin" value={formatPriceLakh(s.potential_margin)} />
      </div>

      <section className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-display font-bold text-base text-gray-900">Inventory Aging</h2>
            <p className="text-xs text-gray-500">Avg {s.avg_days_in_stock} days in stock</p>
          </div>
          <Link to="/admin/inventory" className="text-xs font-semibold text-[#940C2C]">View all →</Link>
        </div>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-3">
          {Object.entries(s.aging).map(([k, v], i) => {
            const colors = ["bg-emerald-50 text-emerald-700", "bg-amber-50 text-amber-700", "bg-orange-50 text-orange-700", "bg-rose-50 text-rose-700", "bg-red-50 text-[#940C2C]"];
            return (
              <div key={k} className={`rounded-lg p-4 ${colors[i]}`}>
                <div className="text-[11px] font-bold uppercase tracking-wider">{k} days</div>
                <div className="mt-1 font-display font-extrabold text-2xl">{v}</div>
              </div>
            );
          })}
        </div>
      </section>

      {s.attention_required.length > 0 && (
        <section className="bg-white rounded-xl border border-rose-200 p-5">
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-[#940C2C]" />
            <h2 className="font-display font-bold text-base text-gray-900">Attention Required ({s.attention_required.length})</h2>
          </div>
          <p className="text-xs text-gray-500 mt-1">Vehicles in stock for 45+ days — review pricing, re-shoot photos, or boost marketing.</p>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-gray-400 uppercase tracking-wider">
                <tr><th className="text-left py-2">Stock</th><th className="text-left py-2">Vehicle</th><th className="text-left py-2">Price</th><th className="text-left py-2">Days</th><th></th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {s.attention_required.slice(0, 10).map((v) => (
                  <tr key={v.id}>
                    <td className="py-2 font-mono text-xs">{v.stock_id}</td>
                    <td className="py-2 font-semibold text-gray-900">{v.title}</td>
                    <td className="py-2 font-bold text-[#940C2C]">{formatPriceLakh(v.price)}</td>
                    <td className="py-2"><span className={`inline-flex items-center gap-1 text-xs font-bold rounded px-2 py-0.5 ${v.days >= 90 ? "bg-red-100 text-red-800" : v.days >= 60 ? "bg-orange-100 text-orange-800" : "bg-amber-100 text-amber-800"}`}><Clock size={11} />{v.days}d</span></td>
                    <td className="py-2 text-right"><Link to={`/admin/inventory/${v.id}`} className="text-xs font-semibold text-[#940C2C]">Review →</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <section className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="font-display font-bold text-base text-gray-900">Recently Added</h2>
          <div className="mt-4 space-y-2">
            {s.recent_cars.map((v) => (
              <Link key={v.id} to={`/admin/inventory/${v.id}`} className="flex items-center gap-3 p-2 rounded hover:bg-gray-50">
                <img src={v.image || "https://placehold.co/80x60"} alt="" className="w-14 h-10 rounded object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-900 truncate">{v.title}</div>
                  <div className="text-xs text-gray-500">{v.stock_id} · {v.status}</div>
                </div>
                <div className="text-sm font-bold text-[#940C2C]">{formatPriceLakh(v.price)}</div>
              </Link>
            ))}
          </div>
        </section>
        <section className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="font-display font-bold text-base text-gray-900">Recently Sold</h2>
          {s.recent_sold.length === 0 ? <p className="text-sm text-gray-400 mt-4">No sales yet.</p> : (
            <div className="mt-4 space-y-2">
              {s.recent_sold.map((v) => (
                <Link key={v.id} to={`/admin/inventory/${v.id}`} className="flex items-center gap-3 p-2 rounded hover:bg-gray-50">
                  <img src={v.image || "https://placehold.co/80x60"} alt="" className="w-14 h-10 rounded object-cover grayscale" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-gray-900 truncate">{v.title}</div>
                    <div className="text-xs text-gray-500">{v.stock_id} · {new Date(v.sold_date).toLocaleDateString()}</div>
                  </div>
                  <div className="text-sm font-bold text-gray-700">{formatPriceLakh(v.price)}</div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

const Kpi = ({ icon: Icon, label, value, primary, warn }) => (
  <div className={`rounded-xl border p-4 ${primary ? "bg-[#940C2C] text-white border-[#940C2C]" : warn ? "bg-amber-50 border-amber-100" : "bg-white border-gray-200"}`}>
    <div className={`flex items-center gap-1.5 text-[10px] uppercase font-bold tracking-wider ${primary ? "text-white/80" : "text-gray-500"}`}>
      <Icon size={12} /> {label}
    </div>
    <div className={`mt-2 font-display font-extrabold text-xl ${primary ? "text-white" : "text-gray-900"}`}>{value}</div>
  </div>
);
